import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Wallet, GraduationCap, Cpu } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function WhyJoinUs() {
  return (
    <section id="why" data-testid="why-section" className="relative py-24 sm:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div {...reveal} className="max-w-3xl mb-16">
          <div className="overline mb-4">Why 1st Choice</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0a0a0a]">
            Four reasons producers <em className="not-italic text-[#121275]">never leave</em>.
          </h2>
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Higher Commissions - hero card col-span-8 */}
          <motion.div
            {...reveal}
            data-testid="why-card-commissions"
            className="md:col-span-8 bg-white border border-black/10 tracing-border min-h-[420px] p-10 lg:p-14 hover:-translate-y-1 transition-transform duration-300"
          >
            <div className="h-full flex flex-col justify-between">
              <div>
                <TrendingUp className="text-[#121275]" size={32} strokeWidth={1.25} />
                <div className="overline mt-8 mb-3">Higher Commissions</div>
                <h3 className="font-display text-4xl lg:text-5xl font-light text-[#0a0a0a] tracking-tight max-w-md">
                  Keep <span className="text-[#121275]">100%</span> of every dollar you close.
                </h3>
                <p className="mt-6 text-black/70 max-w-md font-light">
                  No commission splits, no graduated tiers, no surprise reductions, no franchise
                  royalty. Your commission is yours. We only charge $3,500 a year plus $150 per
                  unit sold.
                </p>
              </div>
              <div className="numeric text-xs text-black/40 tracking-widest uppercase mt-10">
                Industry avg: 70%–80% split · 1st Choice: 100% + flat fee only
              </div>
            </div>
          </motion.div>

          {/* Lower Fees */}
          <motion.div
            {...reveal}
            data-testid="why-card-fees"
            className="md:col-span-4 bg-[#f7f7f7] border border-black/10 p-8 lg:p-10 hover:-translate-y-1 transition-transform duration-300"
          >
            <Wallet className="text-[#121275]" size={28} strokeWidth={1.25} />
            <div className="overline mt-6 mb-3">Lower Fees</div>
            <h3 className="font-display text-2xl lg:text-3xl font-light text-[#0a0a0a] tracking-tight">
              $3,500 / year + $150 per unit. That's it.
            </h3>
            <p className="mt-4 text-black/65 font-light text-sm leading-relaxed">
              No desk fees, no royalty fees, no franchise tax, no E&O markup, no tech fee. Two
              transparent line items cover everything.
            </p>
            <div className="mt-8 pt-6 border-t border-black/10">
              <div className="numeric text-xs text-black/40 uppercase tracking-widest">
                Typical savings vs. franchise
              </div>
              <div className="numeric text-2xl text-[#121275] mt-1">$20k+ / yr</div>
            </div>
          </motion.div>

          {/* Weekly Training */}
          <motion.div
            {...reveal}
            data-testid="why-card-training"
            className="md:col-span-5 bg-[#f7f7f7] border border-black/10 p-8 lg:p-10 hover:-translate-y-1 transition-transform duration-300"
          >
            <GraduationCap className="text-[#121275]" size={28} strokeWidth={1.25} />
            <div className="overline mt-6 mb-3">Weekly Training</div>
            <h3 className="font-display text-2xl lg:text-3xl font-light text-[#0a0a0a] tracking-tight">
              Bi-weekly Zoom training
            </h3>
            <p className="mt-4 text-black/65 font-light text-sm leading-relaxed">
              Two Zoom coaching sessions every month — scripts, listing presentations,
              negotiation, and lead conversion, taught by the agents actually doing the volume.
              Always recorded.
            </p>
          </motion.div>

          {/* Software */}
          <motion.div
            {...reveal}
            data-testid="why-card-software"
            className="md:col-span-7 bg-[#f7f7f7] border border-black/10 p-8 lg:p-10 hover:-translate-y-1 transition-transform duration-300 relative overflow-hidden"
          >
            <Cpu className="text-[#121275]" size={28} strokeWidth={1.25} />
            <div className="overline mt-6 mb-3">Best-in-Class Software</div>
            <h3 className="font-display text-2xl lg:text-3xl font-light text-[#0a0a0a] tracking-tight max-w-xl">
              The complete tech stack — included, not invoiced.
            </h3>
            <p className="mt-4 text-black/65 font-light text-sm leading-relaxed max-w-xl">
              AI-powered CRM, automated lead nurture, MLS integrations, transaction management,
              digital signing, marketing automation, virtual staging, comparative market analysis.
              All bundled. Always updated.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {["Website", "Full CRM", "Auto Social", "MLS Sync", "Marketplaces", "E-Sign"].map((t) => (
                <div
                  key={t}
                  className="numeric text-[10px] uppercase tracking-widest text-black/60 border border-black/10 px-3 py-2 text-center"
                >
                  {t}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
